// Multi-family: Units CRUD page (P1-10). Building selector + occupancy
// (live households) summary. Local-only banner because controller-side
// unit topology binding lands later.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Button, Icon } from '@ng-unifi/ui';
import { CreateUnitBody, UpdateUnitBody } from '@ng-unifi/domain';
import { apiClient, ApiError } from '../lib/api.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { Link } from 'react-router-dom';
import { CrudTable, fieldCls } from '../components/CrudTable.js';
import { PropertyScopeBar, scopedHref, usePropertyScope } from '../components/PropertyScope.js';
import { CrudModal, FormField } from '../components/CrudModal.js';
import { RowActions, Toast } from '../components/RowActions.js';
import { LocalOnlyBanner } from '../components/LocalOnlyBanner.js';

interface UnitRow {
  id: string;
  building_id: string;
  unit_number: string;
  floor: string | null;
  created_at: string;
}

interface BuildingOption {
  id: string;
  name: string;
}

interface HouseholdLite {
  id: string;
  unit_id: string;
  moved_out_at: string | null;
}

async function loadUnits(): Promise<UnitRow[]> {
  const res = await apiClient.get<{ data: UnitRow[] }>('/api/v1/units');
  return res.data;
}

async function loadBuildings(): Promise<BuildingOption[]> {
  const res = await apiClient.get<{ data: BuildingOption[] }>('/api/v1/buildings');
  return res.data;
}

async function loadHouseholds(): Promise<HouseholdLite[]> {
  const res = await apiClient.get<{ data: HouseholdLite[] }>('/api/v1/households');
  return res.data;
}

type ModalKind = 'create' | 'edit' | 'delete';
type ModalState = { kind: ModalKind; row: UnitRow | null };

export function UnitsPage() {
  const [rows, setRows] = useState<UnitRow[] | null>(null);
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [occupancyByUnit, setOccupancyByUnit] = useState<Record<string, number>>(
    {},
  );
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    Promise.all([loadUnits(), loadBuildings(), loadHouseholds().catch(() => [])])
      .then(([units, bs, hs]) => {
        if (cancelled) return;
        setRows(units);
        setBuildings(bs);
        const map: Record<string, number> = {};
        for (const h of hs) {
          if (h.moved_out_at) continue;
          map[h.unit_id] = (map[h.unit_id] ?? 0) + 1;
        }
        setOccupancyByUnit(map);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  // Scoped from Buildings. The filter is client-side because every unit row
  // already carries building_id — no new endpoint needed.
  const { buildingId } = usePropertyScope();
  const visibleRows = buildingId ? (rows ?? []).filter((u) => u.building_id === buildingId) : rows;

  const buildingName = (id: string) =>
    buildings.find((b) => b.id === id)?.name ?? id;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="home" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Units
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Individual rentable units across the property.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setModal({ kind: 'create', row: null })}
          disabled={buildings.length === 0}
        >
          <Icon name="add" />
          <span>Add unit</span>
        </Button>
      </header>

      <LocalOnlyBanner
        title="Local records only"
        body="Units are stored locally — door-binding and controller scope sync land later."
      />

      {toast ? <Toast message={toast} onDismiss={() => setToast(null)} /> : null}

      <PropertyScopeBar
        crumbs={
          buildingId
            ? [
                { label: 'Buildings', href: '/buildings' },
                { label: buildingName(buildingId) },
              ]
            : []
        }
        clearHref="/units"
        countLabel={
          buildingId && rows
            ? `${visibleRows?.length ?? 0} of ${rows.length} units`
            : undefined
        }
      />

      <CrudTable<UnitRow>
        rows={visibleRows}
        error={error}
        emptyMessage={
          buildings.length === 0
            ? 'Add a building first, then come back here to add units.'
            : 'No units yet. Click Add unit to create one.'
        }
        columns={[
          {
            key: 'unit_number',
            header: 'Unit #',
            width: '14%',
            // Onward to the households living in this unit.
            render: (u) => (
              <Link
                to={scopedHref('/households', { building_id: u.building_id, unit_id: u.id })}
                className="rounded-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {u.unit_number}
              </Link>
            ),
          },
          {
            key: 'floor',
            header: 'Floor',
            width: '14%',
            render: (u) =>
              u.floor ?? <span className="text-on-surface-variant">—</span>,
          },
          {
            key: 'building_id',
            header: 'Building',
            width: '34%',
            render: (u) => buildingName(u.building_id),
          },
          {
            key: 'occupancy',
            header: 'Occupancy',
            width: '14%',
            render: (u) => {
              const n = occupancyByUnit[u.id] ?? 0;
              return (
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${
                    n > 0
                      ? 'bg-secondary-container text-on-secondary-container'
                      : 'bg-surface-container-high text-on-surface-variant'
                  }`}
                >
                  {n > 0 ? `${n} household${n === 1 ? '' : 's'}` : 'Vacant'}
                </span>
              );
            },
          },
        ]}
        actions={(u) => (
          <RowActions
            actions={[
              { label: 'Edit', onClick: () => setModal({ kind: 'edit', row: u }) },
              {
                label: 'Delete',
                destructive: true,
                onClick: () => setModal({ kind: 'delete', row: u }),
              },
            ]}
          />
        )}
      />

      {modal ? (
        <UnitModal
          state={modal}
          buildings={buildings}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function UnitModal({
  state,
  buildings,
  onCancel,
  onDone,
}: {
  state: ModalState;
  buildings: BuildingOption[];
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { kind, row } = state;
  const { key: idemKey } = useIdempotencyKey();
  const [buildingId, setBuildingId] = useState(
    row?.building_id ?? buildings[0]?.id ?? '',
  );
  const [unitNumber, setUnitNumber] = useState(row?.unit_number ?? '');
  const [floor, setFloor] = useState(row?.floor ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (kind === 'create') {
        const body = CreateUnitBody.parse({
          building_id: buildingId,
          unit_number: unitNumber.trim(),
          floor: floor.trim() || null,
        });
        await apiClient.withIdempotencyKey(idemKey).post('/api/v1/units', body);
        onDone(`Unit ${body.unit_number} created.`);
        return;
      }
      if (kind === 'edit') {
        const body = UpdateUnitBody.parse({
          unit_number: unitNumber.trim(),
          floor: floor.trim() || null,
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .patch(`/api/v1/units/${row!.id}`, body);
        onDone(`Unit ${unitNumber.trim()} updated.`);
        return;
      }
      if (kind === 'delete') {
        await apiClient.delete(`/api/v1/units/${row!.id}`);
        onDone(`Unit ${row!.unit_number} deleted.`);
        return;
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed',
      );
      setSubmitting(false);
    }
  }

  const title =
    kind === 'create'
      ? 'Add unit'
      : kind === 'edit'
        ? `Edit unit ${row?.unit_number}`
        : `Delete unit ${row?.unit_number}`;
  const tone: 'default' | 'destructive' = kind === 'delete' ? 'destructive' : 'default';
  const icon = kind === 'delete' ? 'delete' : kind === 'edit' ? 'edit' : 'add';
  const submitLabel = kind === 'delete' ? 'Delete' : kind === 'edit' ? 'Save' : 'Create';

  return (
    <CrudModal
      title={title}
      icon={icon}
      tone={tone}
      submitting={submitting}
      error={error}
      submitLabel={submitLabel}
      canSubmit={
        kind === 'delete' ||
        (unitNumber.trim().length > 0 && (kind === 'edit' || buildingId.length > 0))
      }
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      {kind === 'delete' ? (
        <p className="font-body-md text-body-md text-on-surface">
          Deleting unit <strong>{row?.unit_number}</strong> removes it from the
          property roster. Households on this unit must already be moved out.
        </p>
      ) : (
        <>
          {kind === 'create' && (
            <FormField id="unit-building" label="Building">
              <select
                id="unit-building"
                required
                value={buildingId}
                onChange={(e) => setBuildingId(e.target.value)}
                className={fieldCls}
              >
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </FormField>
          )}
          <FormField id="unit-number" label="Unit number">
            <input
              id="unit-number"
              required
              maxLength={40}
              value={unitNumber}
              onChange={(e) => setUnitNumber(e.target.value)}
              className={fieldCls}
            />
          </FormField>
          <FormField id="unit-floor" label="Floor" hint="Optional.">
            <input
              id="unit-floor"
              maxLength={40}
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              className={fieldCls}
            />
          </FormField>
        </>
      )}
    </CrudModal>
  );
}
